import { z } from 'zod';
import { userRoleSchema } from './enums.js';
import { SELECTABLE_UPLOAD_TTL_KINDS, displayNameSchema, provenanceSchema, selectableTtlKindSchema, ttlKindSchema, } from './storage.js';
/**
 * Contrats de la CONSOLE SUPERADMIN (XFILE-09) — source unique front + back.
 *
 * Trois surfaces, toutes réservées au rôle `superadmin` :
 *   - PARAMÉTRAGE (`app_settings`, singleton) : nom affiché, TTL par défaut à l'upload,
 *     interrupteur d'accès communautaire, taille max d'upload effective.
 *   - LOGS : journal d'actions (`audit_log`, insert-only) — listing paginé + effacement.
 *     Rétention 30 j (2ᵉ horloge SPEC §5), appliquée automatiquement par la maintenance.
 *   - PURGE : nettoyage MANUEL des fichiers/dossiers soft-deleted depuis > 30 j (3ᵉ
 *     horloge SPEC §5) — le CONTENU est déjà effacé par la GC (T4) ; ceci purge les LIGNES.
 */
/** Rétention du journal d'actions (jours). 2ᵉ horloge SPEC §5 — auto (maintenance). */
export const AUDIT_RETENTION_DAYS = 30;
/** Âge minimal d'un soft-delete avant purge des lignes (jours). 3ᵉ horloge SPEC §5 — manuel. */
export const SOFT_DELETE_PURGE_AFTER_DAYS = 30;
// ---------------------------------------------------------------------------
// Paramétrage (`app_settings`).
// ---------------------------------------------------------------------------
/**
 * Réglages applicatifs pilotables par le superadmin. `maxUploadBytes` est la limite
 * EFFECTIVE par fichier : le serveur la borne à `MAX_UPLOAD_BYTES` (plafond dur multipart,
 * en env) — une valeur supérieure est refusée en écriture.
 */
export const appSettingsSchema = z.object({
    appName: z.string().min(1).max(100),
    /**
     * Classes de TTL PROPOSÉES « en un clic » à l'upload (cases à cocher superadmin) : un
     * sous-ensemble NON VIDE de {@link SELECTABLE_UPLOAD_TTL_KINDS}. Le serveur DÉDOUBLONNE et
     * garantit ≥ 1 entrée. Défaut : `['1m', 'forever']` (30 jours + éternel).
     */
    uploadTtlKinds: z.array(selectableTtlKindSchema).min(1).max(SELECTABLE_UPLOAD_TTL_KINDS.length),
    /**
     * Durée de conservation PRÉ-SÉLECTIONNÉE à l'upload. `null` = « aucun » (l'utilisateur doit
     * choisir, rien n'est pré-coché — le défaut). Si non `null`, DOIT appartenir à `uploadTtlKinds`
     * (le serveur ramène à `null` sinon). Le filet serveur pour un dépôt sans champ TTL (ex. API
     * pnyx) reste `DEFAULT_TTL_KIND`, indépendant de ce réglage d'UI.
     */
    defaultTtlKind: ttlKindSchema.nullable(),
    communityAccessEnabled: z.boolean(),
    /**
     * Propose, sur l'écran de RÉSULTAT d'un dépôt d'IMAGE, un bouton « copier le lien [img] »
     * (BBCode `[img]<url>[/img]` pour forums). Défaut : ACTIVÉ. N'a d'effet que pour un lien
     * de fichier image (le serveur ne pose `ShareLink.imageEmbeddable` que dans ce cas).
     */
    bbcodeImageLinkEnabled: z.boolean(),
    maxUploadBytes: z
        .number()
        .int()
        .positive()
        .max(1099511627776), // borne de sanité (1 Tio) ; le serveur borne au plafond dur réel.
});
/**
 * Corps de `PUT /api/admin/settings` — remplacement complet des réglages. Même forme que
 * `appSettingsSchema` (le serveur clampe `maxUploadBytes` au plafond dur).
 */
export const updateAppSettingsRequestSchema = appSettingsSchema;
/**
 * Réponse de `GET /api/config` (PUBLIC) — configuration d'app nécessaire aux écrans de dépôt
 * AVANT d'écrire : nom affiché + durées de conservation proposées (`uploadTtlKinds`) + durée
 * pré-sélectionnée (`defaultTtlKind`, `null` = « aucun »). Ne révèle aucun réglage sensible
 * (taille max, interrupteurs) — seulement ce que le formulaire d'upload doit connaître.
 */
export const appConfigSchema = z.object({
    appName: z.string(),
    uploadTtlKinds: z.array(ttlKindSchema).min(1),
    defaultTtlKind: ttlKindSchema.nullable(),
    /**
     * Plafond de taille d'un fichier déposé, en octets — le plafond EFFECTIF appliqué par le serveur
     * (réglage `maxUploadBytes` déjà clampé au plafond dur). **Consommé par le LOT 2.**
     *
     * MANQUE COMBLÉ : le client ignore STRUCTURELLEMENT cette limite. Il hache donc le fichier
     * (SHA-256 sur plusieurs centaines de Mo), ouvre la requête, POUSSE TOUS LES OCTETS sur le réseau,
     * et n'apprend qu'au refus final qu'ils étaient de trop. Sur le cas de référence (450 Mo), c'est
     * un transfert complet pour rien — et l'utilisateur n'a aucun moyen de le prévoir. Connu à
     * l'avance, le fichier est écarté dès la sélection, avant le moindre calcul.
     *
     * OPTIONNEL, donc additif (les clients antérieurs continuent de typechecker et ignorent le champ).
     *
     * DIVULGATION : aucune. `/api/config` est déjà PUBLIC, et un plafond d'upload n'est pas un secret —
     * c'est une règle que tout déposant découvre de toute façon en la heurtant. Les réglages réellement
     * sensibles (interrupteurs, jetons) restent hors de cette route.
     */
    maxUploadBytes: z.number().int().positive().optional(),
});
// ---------------------------------------------------------------------------
// Journal d'actions (`audit_log`).
// ---------------------------------------------------------------------------
/**
 * Actions connues émises au journal (référence pour les libellés i18n de la console).
 * `action` reste une chaîne LIBRE côté transport (forward-compatible : un client plus
 * ancien affiche l'action brute si une nouvelle valeur apparaît).
 */
export const AUDIT_ACTIONS = [
    'auth.login',
    'auth.password_change',
    'file.upload',
    'file.delete',
    'file.extend_ttl',
    'files.bulk_delete',
    'files.bulk_move',
    'folder.create',
    'folder.rename',
    'folder.move',
    'folder.delete',
    'folder.extend_ttl',
    'share.create',
    'settings.update',
    'user.create',
    'user.password_reset',
    'user.delete',
    'community.rotate',
    'community.revoke',
    'community.redeem',
    'community.link_create',
    'community.link_revoke',
    'community.deposit',
    'community.deposit_api',
    'community.deposit_ip_set',
    'admin.logs_clear',
    'admin.purge_soft_deleted',
];
/**
 * Une entrée du journal d'actions telle qu'exposée à la console. `actorEmail` est
 * résolu par jointure (`null` si l'acteur a été supprimé ou pour une action système).
 */
export const auditLogEntrySchema = z.object({
    id: z.uuid(),
    action: z.string(),
    actorId: z.uuid().nullable(),
    actorEmail: z.string().nullable(),
    targetType: z.string(),
    targetId: z.string().nullable(),
    summary: z.string().nullable(),
    /** PROVENANCE de l'action (`web` / `pnyx`) quand elle est connue ; `null` sinon (T13). */
    provenance: provenanceSchema.nullable(),
    createdAt: z.string(),
});
/**
 * Requête de listing `GET /api/admin/logs` : page de `limit` entrées les plus récentes,
 * pagination par CURSEUR. Filtres EN BASE optionnels (T13) : par acteur (`userId`), type d'action
 * exact (`action`) et provenance.
 *
 * ⚠ `before` est un CURSEUR OPAQUE, et non plus un horodatage ISO nu (lot 2) : il porte le couple
 * `<ISO>|<uuid>`, sans quoi des entrées partageant la même `created_at` à la milliseconde près
 * étaient sautées ou rendues deux fois d'une page à l'autre. Le client le RENVOIE TEL QUEL, sans
 * jamais le parser ni le reconstruire.
 */
export const auditLogQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: z.string().optional(),
    userId: z.uuid().optional(),
    action: z.string().min(1).max(100).optional(),
    provenance: provenanceSchema.optional(),
});
/** Réponse de `GET /api/admin/logs` : la page + le curseur suivant (`null` = fin). */
export const auditLogPageSchema = z.object({
    entries: z.array(auditLogEntrySchema),
    nextBefore: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Purge des soft-deleted (> 30 j).
// ---------------------------------------------------------------------------
/**
 * Comptes de lignes soft-deleted éligibles à la purge (aperçu, `GET`) ou effectivement
 * purgées (résultat, `POST`). Le contenu disque est déjà effacé par la GC (T4) : ceci ne
 * porte que sur les LIGNES de métadonnées (`files` / `folders`).
 */
export const purgeSoftDeletedResultSchema = z.object({
    files: z.number().int().nonnegative(),
    folders: z.number().int().nonnegative(),
    /**
     * Blobs ORPHELINS effacés par la passe de réconciliation lancée après la purge (T13) : des
     * binaires que plus aucune ligne ne référence. `0` pour l'APERÇU (`GET`, n'exécute rien).
     */
    orphanBlobs: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Gestion des utilisateurs (superadmin).
// ---------------------------------------------------------------------------
/** Un compte utilisateur tel que listé dans la console. Ne porte jamais de secret. */
export const adminUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    createdAt: z.string(),
    /**
     * Compte PROTÉGÉ de toute gestion destructive (réinitialisation de mot de passe /
     * suppression) : soit son rôle est `superadmin`, soit c'est le compte système
     * « communauté ». Le client masque les actions destructives quand `system` est vrai ;
     * le serveur applique la garde INDÉPENDAMMENT (403), le drapeau n'est qu'un indice UI.
     */
    system: z.boolean(),
    /**
     * Compte DÉSACTIVÉ (XFILE-40) : horodatage de la désactivation, `null`/absent = compte actif.
     * **Renseigné par le LOT 2.**
     *
     * OPTIONNEL, donc additif. Correspond à `users.deleted_at` en base — colonne INCHANGÉE, aucune
     * migration : c'est le VOCABULAIRE qui est corrigé, pas le stockage (cf.
     * {@link updateUserRequestSchema}).
     */
    deactivatedAt: z.string().nullable().optional(),
});
/** Réponse de `GET /api/admin/users` : les comptes existants. */
export const adminUsersResponseSchema = z.object({
    users: z.array(adminUserSchema),
});
/**
 * Query de `GET /api/admin/users` — inclure les comptes DÉSACTIVÉS.
 *
 * Nécessaire pour que la réactivation soit atteignable : on ne peut pas réactiver un compte qu'on ne
 * voit pas. Par DÉFAUT `false`, donc le listing actuel (comptes actifs seuls) est INCHANGÉ tant que
 * le client ne demande rien — additif au sens strict.
 *
 * ⚠ LISTE BLANCHE, PAS `z.coerce.boolean()` (XFILE-60). `z.coerce.boolean()` est `Boolean(v)`, et
 * une query-string ne transporte que des CHAÎNES : toute chaîne non vide est donc VRAIE. Vérifié —
 * `?includeInactive=false` rendait `true`, tout comme `=0` et `=no`. Demander explicitement de NE
 * PAS inclure les comptes désactivés les incluait : le contrat affirmait l'exact contraire de ce
 * qu'il faisait, sur le listing d'une console d'administration.
 *
 * Le défaut `false` masquait le défaut plutôt qu'il ne le corrigeait : `fetchUsers` OMET le
 * paramètre quand il ne le veut pas (`admin-api.ts` ne pose que `?includeInactive=true`), donc
 * l'écran des comptes n'a jamais été touché. Le piège attendait le prochain appelant — celui qui
 * passerait un booléen sérialisé sans y penser, et obtiendrait des comptes désactivés dans une
 * liste où il croit n'avoir que des actifs.
 *
 * Même remède que `?only=folders` (`listingOnlySchema`, `storage.ts`) : une liste blanche REFUSE
 * (400) ce qu'elle ne connaît pas, au lieu de deviner. `.transform()` rend un vrai booléen, donc
 * l'appelant serveur est inchangé.
 */
export const adminUsersQuerySchema = z.object({
    includeInactive: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
});
/**
 * Corps de `PATCH /api/admin/users/:id` (superadmin) — met à jour un compte : son RÔLE et/ou son
 * état ACTIF. **Consommé par le LOT 2.** Les deux champs sont optionnels ; un corps vide est un
 * no-op accepté.
 *
 * DÉCISION PRODUIT — « supprimer » un compte n'a jamais rien supprimé. `DELETE /api/admin/users/:id`
 * pose `users.deleted_at` et révoque les sessions : le compte cesse de pouvoir se connecter, mais
 * ses fichiers, ses dossiers, son espace personnel et ses traces d'audit restent intacts — c'est une
 * DÉSACTIVATION. Le mot « supprimer » promettait un effacement qui n'avait pas lieu, ce qui est le
 * pire sens à donner à un bouton destructif : l'exploitant croit avoir effacé des données qui sont
 * toujours là. Le vocabulaire des contrats et de l'UI devient donc « désactiver / réactiver ».
 *
 * ⚠ LE COMPORTEMENT SERVEUR NE CHANGE PAS, et aucune migration n'est requise : même colonne
 * (`users.deleted_at`), même effet, même révocation de sessions. On corrige ce qu'on en DIT, pas ce
 * qu'on en fait. `DELETE /api/admin/users/:id` reste en place (désactivation) ; `active: false` en
 * est le synonyme, et `active: true` est la RÉCIPROQUE qui manquait — jusqu'ici la désactivation
 * était un aller SANS RETOUR par l'interface, alors que la remettre à `NULL` a toujours suffi.
 *
 * `superadmin` reste hors d'atteinte (comme à la création) : ni attribuable, ni retirable ici. Les
 * comptes PROTÉGÉS (`system`) sont refusés par le serveur en 403, indépendamment de ce contrat.
 */
export const updateUserRequestSchema = z.object({
    role: z.enum(['admin', 'user']).optional(),
    active: z.boolean().optional(),
});
/**
 * Corps de `POST /api/admin/users` (superadmin) — crée un compte. Le rôle `superadmin`
 * n'est PAS créable ici (réservé au système / seed) : seuls `admin` et `user` sont
 * acceptés. Réponse = `adminUserSchema`.
 */
export const createUserRequestSchema = z.object({
    email: z.email().max(255),
    /**
     * RESSERRÉ (XFILE-25) : `displayNameSchema` au lieu d'un `z.string().trim().min(1).max(100)`.
     * Mêmes bornes (1..100), mais il interdit désormais les séparateurs de chemin, les caractères de
     * contrôle et `.`/`..` — car ce nom devient le NOM DU DOSSIER racine de l'espace personnel
     * (`ensureUserRoot`). Voir le commentaire de `displayNameSchema` pour la raison complète, et
     * pourquoi les schémas de LECTURE restent volontairement permissifs (comptes déjà en base).
     */
    displayName: displayNameSchema,
    role: z.enum(['admin', 'user']),
    password: z.string().min(8).max(200),
});
/**
 * Corps de `PUT /api/admin/users/:id/password` (superadmin) — réinitialise le mot de passe
 * d'un compte. Le serveur HACHE (Argon2id) puis révoque toutes les sessions du compte.
 * Un compte PROTÉGÉ (`superadmin` / système communauté) est refusé (403). La suppression
 * (`DELETE /api/admin/users/:id`) n'a pas de corps.
 */
export const updateUserPasswordRequestSchema = z.object({
    password: z.string().min(8).max(200),
});
// ---------------------------------------------------------------------------
// Liste globale des fichiers du dépôt (console superadmin, T13).
// ---------------------------------------------------------------------------
/**
 * Colonnes de TRI de `GET /api/admin/files` — LISTE BLANCHE (le serveur mappe chaque valeur vers
 * une colonne SQL via un objet, jamais d'interpolation de la valeur brute). Défaut : `created`.
 */
export const adminFilesSortSchema = z.enum([
    'name',
    'size',
    'consultations',
    'lastConsulted',
    'created',
    'provenance',
]);
/**
 * Requête de `GET /api/admin/files` : tri (colonne + direction) sur liste blanche + pagination
 * OFFSET. `limit` borné à 100 (garde-fou). Coercition des query-strings (`z.coerce`).
 */
export const adminFilesQuerySchema = z.object({
    sort: adminFilesSortSchema.default('created'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * Une ligne de la liste globale des fichiers ACTIFS. `folderPath` est le chemin humain du dossier
 * parent (ex. `2026/07`), `null` à la racine. `hasContent` = le binaire est encore sur disque
 * (`storage_key` non NULL). Ne porte jamais la `storage_key` interne.
 */
export const adminFileRowSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    folderId: z.string().nullable(),
    folderPath: z.string().nullable(),
    sizeBytes: z.number(),
    mimeType: z.string(),
    provenance: provenanceSchema,
    consultationCount: z.number(),
    lastConsultedAt: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string().nullable(),
    hasContent: z.boolean(),
});
/** Réponse de `GET /api/admin/files` : la page + le total de fichiers actifs (pour la pagination). */
export const adminFilesResponseSchema = z.object({
    rows: z.array(adminFileRowSchema),
    total: z.number(),
});
//# sourceMappingURL=admin.js.map